import { useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";

/**
 * useBirthDetailsPrefill — one-shot prefill of any tool's form from the
 * signed-in user's saved profile (name + birth_details).
 *
 * Pages with custom field shapes (separate hour/minute, name+dob, multi-
 * partner forms, etc.) use this hook instead of the more opinionated
 * `usePrefillBirthDetails(setForm, setPlaceQuery)` / `useBirthDetailsForm()`.
 *
 * Usage:
 *   useBirthDetailsPrefill((bd, user) => {
 *     if (user?.name) setName((p) => p || user.name);
 *     if (bd?.date)   setDate((p) => p || bd.date);
 *     if (bd?.time) {
 *       const [hh, mm] = String(bd.time).split(":");
 *       setHour((p) => p !== "" ? p : (hh || ""));
 *       setMinute((p) => p !== "" ? p : (mm || ""));
 *     }
 *     if (bd?.place)    setPlace((p) => p || bd.place);
 *     if (bd?.timezone) setTimezone((p) => p || bd.timezone);
 *   });
 *
 * Fires exactly once — the first time `user.birth_details` (or `user.name`,
 * if no birth_details) becomes available. Subsequent user edits are never
 * overwritten. Signed-out users see a no-op.
 */
export default function useBirthDetailsPrefill(handler) {
  const { user } = useAuth();
  const done = useRef(false);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (done.current) return;
    const bd = user?.birth_details;
    const hasBD = !!(bd?.date && bd?.time && bd?.place);
    const hasName = !!user?.name;
    if (!hasBD && !hasName) return;
    done.current = true;
    handlerRef.current?.(bd || null, user);
  }, [user]);
}
